import { motion } from "framer-motion";
import { ArrowDown, ArrowUpRight, CloudCog, Database, BrainCircuit, Layers } from "lucide-react";
import { Logo } from "../landing/Logo";
import { Reveal } from "../landing/Reveal";

const PARTNERS = ["Microsoft Azure", "SAP", "Zoho", "OpenAI", "Stripe", "Razorpay"];

const STACK = [
  { icon: Logo, label: "FUNZ", sub: "Experience layer" },
  { icon: Layers, label: "Zoho One", sub: "Business OS" },
  { icon: Database, label: "SAP BTP", sub: "Enterprise data" },
  { icon: BrainCircuit, label: "Azure AI", sub: "Intelligence" },
];

const METRICS = [
  { v: "$2.1M", l: "GMV processed" },
  { v: "480+", l: "Campuses live" },
  { v: "68%", l: "MoM retention" },
  { v: "25K+", l: "Active students" },
];

export const Enterprise = () => (
  <section id="enterprise" data-testid="enterprise-section" className="relative overflow-hidden py-24 sm:py-32">
    <div aria-hidden="true" className="absolute -right-40 top-16 h-[420px] w-[420px] rounded-full bg-[#635BFF]/10 blur-[150px]" />
    <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
      <Reveal className="text-center">
        <p className="font-jbmono text-xs uppercase tracking-[0.3em] text-[#00D26A]">
          Enterprise Integration
        </p>
        <h2 className="mx-auto mt-4 max-w-2xl font-display text-3xl font-extrabold tracking-tight text-white sm:text-4xl lg:text-5xl">
          Startup soul. Enterprise spine.
        </h2>
      </Reveal>

      <Reveal delay={0.08}>
        <div className="mt-12 flex flex-wrap items-center justify-center gap-3" data-testid="partner-logos">
          {PARTNERS.map((p) => (
            <span
              key={p}
              data-testid={`partner-${p.toLowerCase().replace(" ", "-")}`}
              className="rounded-full border border-white/10 bg-[#111827] px-6 py-3 font-display text-sm font-bold tracking-wide text-slate-500 transition-colors hover:border-[#635BFF]/50 hover:text-white"
            >
              {p}
            </span>
          ))}
        </div>
      </Reveal>

      <div className="mt-16 grid grid-cols-1 items-center gap-14 lg:grid-cols-2">
        <Reveal>
          <div className="mx-auto flex max-w-xs flex-col items-center" data-testid="architecture-stack">
            {STACK.map((s, i) => (
              <div key={s.label} className="flex w-full flex-col items-center">
                <div className="flex w-full items-center gap-4 rounded-2xl border border-white/10 bg-[#111827]/90 px-6 py-4 backdrop-blur-xl">
                  {s.icon === Logo ? (
                    <Logo className="h-8 w-8" />
                  ) : (
                    <s.icon className="h-6 w-6 text-[#00D26A]" />
                  )}
                  <div>
                    <p className="font-display text-base font-bold text-white">{s.label}</p>
                    <p className="font-jbmono text-[10px] uppercase tracking-widest text-slate-500">{s.sub}</p>
                  </div>
                </div>
                {i < STACK.length - 1 && (
                  <motion.div
                    aria-hidden="true"
                    animate={{ y: [0, 5, 0], opacity: [0.4, 1, 0.4] }}
                    transition={{ duration: 1.6, repeat: Infinity, delay: i * 0.3, ease: "easeInOut" }}
                    className="py-1.5"
                  >
                    <ArrowDown className="h-4 w-4 text-[#635BFF]" />
                  </motion.div>
                )}
              </div>
            ))}
          </div>
        </Reveal>

        <Reveal delay={0.12}>
          <div id="investors" data-testid="investors-strip" className="scroll-mt-32">
            <h3 className="font-display text-2xl font-extrabold tracking-tight text-white sm:text-3xl">
              Why investors watch FUNZ.
            </h3>
            <p className="mt-4 max-w-md text-sm leading-relaxed text-slate-400 sm:text-base">
              Edtech, fintech, food and the creator economy — one network where every
              new node raises the value of all the others.
            </p>
            <div className="mt-8 grid grid-cols-2 gap-4">
              {METRICS.map((m, i) => (
                <div
                  key={m.l}
                  data-testid={`investor-metric-${i}`}
                  className={`rounded-3xl border border-white/10 bg-gradient-to-b from-white/[0.06] to-white/[0.02] p-7 backdrop-blur-xl transition-colors hover:border-[#00D26A]/40 ${
                    i % 2 === 1 ? "sm:translate-y-5" : ""
                  }`}
                >
                  <p className="font-display text-3xl font-black tracking-tight text-white sm:text-4xl">{m.v}</p>
                  <p className="mt-2 font-jbmono text-[10px] uppercase tracking-widest text-slate-500">{m.l}</p>
                </div>
              ))}
            </div>
            <button
              data-testid="investor-deck-cta"
              className="glow-btn-z group mt-9 inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-[#635BFF] to-[#00D26A] px-8 py-3.5 font-display text-xs font-bold uppercase tracking-wider text-white"
            >
              Request the Deck
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
            </button>
            <p className="mt-5 font-jbmono text-[10px] uppercase tracking-widest text-slate-600">
              Illustrative metrics for demo purposes
            </p>
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);
